// import { useState, useEffect } from "react";

// /**
//  * Custom hook for fetching data
//  * @param {Object} [options] - Optional configurations
//  * @param {boolean} [options.enabled=true] - If false, the fetch won't trigger automatically
//  * @param {Array} [options.dependencies=[]] - Dependencies that trigger re-fetching
//  * @param {any} [options.initialData=null] - Initial state for data
//  * @param {Function} [options.fetchFunction=null] - Async function to fetch data
//  * @returns {{
//  *   data: any,
//  *   error: Error | null,
//  *   isLoading: boolean,
//  *   refetch: Function,
//  *   updateData: Function
//  * }}
//  */
// const useFetch = (options = {}) => {
//   const {
//     enabled = true,
//     dependencies = [],
//     initialData = null,
//     fetchFunction = () => {},
//   } = options;

//   const [data, setData] = useState(initialData);
//   const [error, setError] = useState(null);
//   const [isLoading, setIsLoading] = useState(false);

//   const fetchData = async () => {
//     setIsLoading(true);
//     try {
//       const result = await fetchFunction();
//       setData(result);
//       setError(null);
//     } catch (err) {
//       setError(err);
//       setData(null);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (enabled) {
//       fetchData();
//     }
//   }, dependencies);

//   return { data, error, isLoading, refetch: fetchData, updateData: setData };
// };

// export default useFetch;

// =========================> bottom hooks for cache system

import { useState, useEffect } from "react";

const cache = new Map(); // Cache to store fetched data

/**
 * Custom hook for fetching data with caching and isFetching state
 * @param {Object} [options] - Optional configurations
 * @param {boolean} [options.enabled=true] - If false, the fetch won't trigger automatically
 * @param {Array} [options.dependencies=[]] - Dependencies that trigger re-fetching
 * @param {any} [options.initialData=null] - Initial state for data
 * @param {Function} [options.fetchFunction=null] - Async function to fetch data
 * @returns {{
 *   data: any,
 *   error: Error | null,
 *   isLoading: boolean,
 *   refetch: Function,
 *   updateData: Function
 *   isFetching: boolean,
 * }} State and functions for fetching data
 */
const useFetch = (options = {}) => {
  const {
    enabled = true,
    dependencies = [],
    initialData = null,
    fetchFunction = () => {},
  } = options;

  const cacheKey = JSON.stringify(dependencies); // Unique key based on dependencies
  const cachedData = cache.get(cacheKey);
  const isCacheAvailable = cache.has(cacheKey);

  const [data, setData] = useState(cachedData || initialData);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(!isCacheAvailable); // Only true for new cache key
  const [isFetching, setIsFetching] = useState(false);

  const fetchData = async () => {
    setIsFetching(true);
    if (!isCacheAvailable) setIsLoading(true); // Only show loading if no cached data
    let result;
    try {
      result = await fetchFunction();
      setData(result);
      setError(null);
      cache.set(cacheKey, result); // Update cache
    } catch (err) {
      result = err;
      setError(err);
      setData(null);
    } finally {
      setIsFetching(false);
      setIsLoading(false);
      return { status: "ok", res: result };
    }
  };

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    if (isCacheAvailable) {
      setData(cachedData); // Use cached data instantly
      setIsLoading(false); // Prevent loading when cache exists
    }
    fetchData(); // Always fetch fresh data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...dependencies]);

  /**
   * Update data by applying a modification function or setting a new value.
   * @param {Function | Object} updater - A function that receives current data and returns modified data, or a new object to replace the data.
   */
  const updateData = (updater) => {
    setData((prevData) => {
      const updatedData =
        typeof updater === "function" ? updater(prevData) : updater;
      cache.set(cacheKey, updatedData); // Ensure cache stays updated
      return updatedData;
    });
  };

  return {
    data,
    error,
    isLoading,
    isFetching,
    refetch: fetchData,
    updateData,
  };
};

export default useFetch;
